import { useEffect, useRef } from "react";

import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { sortByStatus } from "@intelligems/ig-utils/experience-status";
import { useQueryClient } from "@tanstack/react-query";

import { useGetExperiencesList } from "../experiences-list/get-experiences-list";
import { experienceKeys } from "../queryKeys";

import { getExperience } from "./get-experience";

export const usePrefetchPendingExperiences = async (
  category: ExperienceCategory
) => {
  const experiencesList = useGetExperiencesList({ category });
  const queryClient = useQueryClient();
  const fetchingRef = useRef(false);
  const prefetchedPendingExperienceIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const fetchData = async () => {
      if (experiencesList.isSuccess) {
        // Only prefetch the 50 most recently-created pending experiences.
        // Prefetching every pending experience floods the API (a full
        // GET /v2/experiences/:id each) and is wasted work for tests the user
        // is unlikely to open. staleTime keeps repeated list updates from
        // re-issuing these requests; the detail is refreshed by its own
        // mutations.
        const pendingToPrefetch = sortByStatus(
          experiencesList.data.experiencesList.filter(
            (exp) => exp.status === "pending"
          )
        )
          .slice(0, 50)
          .filter(
            (exp) => !prefetchedPendingExperienceIdsRef.current.has(exp.id)
          );
        await Promise.all(
          pendingToPrefetch.map((exp) => {
            prefetchedPendingExperienceIdsRef.current.add(exp.id);
            return queryClient.prefetchQuery({
              queryKey: experienceKeys.one(exp.id),
              queryFn: async () => getExperience(exp.id),
              staleTime: 5 * 60 * 1000,
            });
          })
        );
      }
    };

    void fetchData();
  }, [experiencesList.isSuccess, experiencesList.data, queryClient]);

  useEffect(() => {
    if (
      experiencesList.hasNextPage &&
      !experiencesList.isFetchingNextPage &&
      !fetchingRef.current
    ) {
      fetchingRef.current = true;
      void experiencesList.fetchNextPage().finally(() => {
        fetchingRef.current = false;
      });
    }
  }, [
    experiencesList.fetchNextPage,
    experiencesList.hasNextPage,
    experiencesList.isFetchingNextPage,
  ]);
};
